'use client';

import Image from "next/image";
import { Contact } from "@/components/Contact";
import { motion } from "framer-motion";

const values = [
  {
    title: "Authenticity",
    description: "We believe in telling real stories that resonate with real people. No gimmicks, just genuine connections."
  },
  {
    title: "Innovation", 
    description: "We push creative boundaries while staying grounded in strategic thinking and measurable results."
  },
  {
    title: "Partnership",
    description: "Your success is our success. We work as an extension of your team, fully invested in your goals."
  },
  {
    title: "Excellence",
    description: "We hold ourselves to the highest standards in everything we do, from strategy to execution."
  }
];

const team = [
  {
    name: "Kennedy Maombi",
    role: "Founder",
    bio: "Kennedy drives our vision for innovative digital solutions and authentic storytelling. His entrepreneurial spirit and fresh perspective help businesses thrive in the digital landscape."
  },
  {
    name: "Re'Auna Jones",
    role: "Head of Client Success",
    bio: "Re'Auna builds exceptional support systems for e-commerce brands. Her expertise in customer experience helps our clients create meaningful connections and lasting relationships."
  },
  {
    name: "Kyle Westerfield",
    role: "Social Media Strategist",
    bio: "Kyle specializes in crafting engaging social media strategies that drive brand awareness and audience engagement. His expertise in content creation and platform optimization helps brands build strong online communities."
  }
];

export function AboutContent() {
  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden" style={{ backgroundColor: 'rgb(245,245,247)' }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10"
        >
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight uppercase" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
              Who We Are
            </h1>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: 'rgba(0,0,0,0.7)' }}>
              Your creative partner helping brands stand out, make an impact, and connect with the right audience.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center"
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight uppercase" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
                Our Story
              </h2>
              <div className="space-y-8 text-lg" style={{ color: 'rgba(0,0,0,0.7)' }}>
                <p>
                  Founded in 2019, Sovereign Creative Agency emerged from seeing how many local businesses struggled to establish their digital presence. We believed that every brand, regardless of size, deserves access to quality creative services.
                </p>
                <p>
                  As a young agency, we bring fresh energy and modern perspectives to brand storytelling. Our approach combines contemporary design trends with practical, results-driven strategies.
                </p>
                <p>
                  Today, we work closely with businesses in our community, helping them build strong digital foundations and authentic brand identities that resonate with their audiences.
                </p>
              </div>
            </div>
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="relative h-[500px] md:h-[700px] rounded-3xl overflow-hidden bg-[#171A20]"
            >
              <Image
                src="/SCAbanner.png"
                alt="Our Team"
                fill
                className="object-cover transition-transform duration-500 hover:scale-105"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 relative" style={{ backgroundColor: 'rgb(245,245,247)' }}>
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10 relative">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8 uppercase" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
              Our Values
            </h2>
            <p className="text-lg md:text-xl" style={{ color: 'rgba(0,0,0,0.7)' }}>
              These core principles guide everything we do
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white backdrop-blur-sm rounded-3xl p-10 hover:bg-gray-50 transition-all duration-300 border border-gray-200"
              >
                <h3 className="text-2xl font-bold mb-4" style={{ color: 'rgb(29,29,31)' }}>{value.title}</h3>
                <p className="leading-relaxed" style={{ color: 'rgba(0,0,0,0.7)' }}>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8 uppercase" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
              Leadership
            </h2>
            <p className="text-lg md:text-xl" style={{ color: 'rgba(0,0,0,0.7)' }}>
              Meet our team
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="bg-white rounded-3xl p-10 border border-[#171A20]/10 hover:border-[#171A20]/20 transition-all duration-300"
              >
                {/* Name */}
                <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: 'rgb(29,29,31)' }}>
                  {member.name}
                </h3>
                
                {/* Role */}
                <p className="text-sm font-medium uppercase tracking-wider mb-8" style={{ color: 'rgba(0,0,0,0.56)', letterSpacing: '0.05em' }}>
                  {member.role}
                </p>
                
                {/* Bio */}
                <p className="text-base leading-relaxed" style={{ color: 'rgba(0,0,0,0.7)' }}>
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      <Contact />
    </main>
  );
} 